<template>
  <qd-collapse
    class="card"
    animation="slide">
    <template #trigger="props">
      <div class="card-header header-card header-recap-affiche"
        role="button"
        aria-controls="contentIdForA11y3">
        <p class="card-header-title">
          {{ $t('catalogue.formatsAffiche.recap.title')}}
        </p>
        <a class="card-header-icon">
          <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
        </a>
      </div>
    </template>
    <div class="card-content">
      <div class="columns">
        <b-field class="column is-6">
          <span class="has-text-default has-text-weight-bold mgr-20px mgt-5px">
            {{ $t('catalogue.dateVisu') }}
          </span>
          <b-datepicker show-week-number
            nearby-selectable-month-days
            icon="calendar-today"
            @input="updateDateVisualisation"
            :value="dateVisualisation"
            :editable="true">
          </b-datepicker>
        </b-field>
      </div>
      <v-client-table
        :data="recapFormatAffiche"
        :columns="columns"
        :options="options"
        class="displayNoneVuePagination">
        <template #appendBody>
          <table-footer-totaux
            :columns="columns"
            :target="['quantite']"
            :items="recapFormatAffiche"
            :formatage="(val) => val">
          </table-footer-totaux>
        </template>
      </v-client-table>
    </div>
  </qd-collapse>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import { INFINITE_DAY } from '@/configuration/general';
import * as tableConfig from '@/configuration/clientTableConf';
import TableFooterTotaux from '@/components/general/tableFooterTotaux';
import messageErreur from '@/services/general/messageErreur';
import cloneDeep from 'lodash/cloneDeep';
import { getAll as getAllFormat } from '@/api/formatAffiche/index';

const Societe = createNamespacedHelpers('societe');

export default {
  name: 'RecapReseauFormatAffiche',
  props: {
    produit: {
      type: Object,
      required: true,
    },
    dateVisualisation: {
      type: Date,
      required: false,
    },
  },
  components: {
    TableFooterTotaux,
  },
  data() {
    return {
      recapFormatAffiche: [],
    };
  },
  methods: {
    async updateDateVisualisation(newValue) {
      await this.genererRecapData(newValue);
      this.$emit('updateDateVisualisation', newValue);
    },
    async genererRecapData(dateVisu) {
      const loadingComponent = this.$buefy.loading.open();
      const affectationsFiltrees = this.produit.produitReseau.affectationsReseauFace
        .filter(aff => {
          const dateFin = aff.dateFin ? moment(aff.dateFin) : moment(INFINITE_DAY);
          return dateVisu ? moment(dateVisu).isBetween(moment(aff.dateDebut), dateFin, 'day', '[]') : true;
        });
      const formatAfficheIds = Array.from(new Set(
        affectationsFiltrees
          .reduce((acc, val) => {
            const id = val.produit.produitFace.face?.materiel?.materielTechnique?.formatAfficheId;
            if (id) acc.push(id);
            return acc;
          }, []),
      ));
      try {
        if (!formatAfficheIds.length) return;
        const formatsAffiches = (await getAllFormat({
          limit: tableConfig.NO_PAGINATION,
          query: {
            societeId: this.societeActiveId,
            formatAfficheIds: formatAfficheIds,
          },
        })).data.result.items;
        const formats = affectationsFiltrees.reduce((acc, val) => {
          const id = val.produit.produitFace.face?.materiel?.materielTechnique?.formatAfficheId;
          const format = id ? formatsAffiches
            .find(f => f.id === id) : null;
          if (format) {
            if (!acc[format.id]) acc[format.id] = { formatAffiche: format.libelle, quantite: 0 };
            acc[format.id].quantite += 1;
          }
          return acc;
        }, {});

        this.recapFormatAffiche = Object.values(formats);
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    columnsData() {
      return [
        { name: 'formatAffiche' },
        { name: 'quantite' },
      ];
    },
    columns() {
      const columns = this.columnsData.map((column) => column.name);
      return columns;
    },
    options() {
      const texts = cloneDeep(tableConfig.TEXTS);
      texts.count = '';
      const headings = this.columnsData.reduce((acc, column) => {
        const key = `catalogue.formatsAffiche.recap.${column.name}`;
        acc[column.name] = this.$te(key) ? this.$t(key) : '';
        return acc;
      }, {});
      return {
        texts,
        headings,
        sortable: [],
        filterable: false,
        sortIcon: tableConfig.SORT_ICON,
        perPageValues: tableConfig.NO_PER_PAGE_VALUE,
        perPage: tableConfig.RESULTS_PER_PAGE,
        cellClasses: {
          quantite: [{ class: 'has-text-right', condition: () => true }],
        },
        columnsClasses: {
          quantite: 'colonneSelected',
        },
      };
    },
  },
  async mounted() {
    await this.genererRecapData(this.dateVisualisation);
  },
};

</script>
